import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Button, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField, IconButton, Alert, Switch, FormControlLabel,
} from '@mui/material';
import { Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { vmTypesAPI } from '../../services/api';
import ConfirmDialog from '../../components/ConfirmDialog';
import { VMType } from '../../types';

interface VMTypeFormData {
  vmName: string;
  cpu: number | string;
  ram: number | string;
  isActive: boolean;
  displayOrder: number;
}

const VMTypes = () => {
  const [vmTypes, setVMTypes] = useState<VMType[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [openDialog, setOpenDialog] = useState(false);
  const [editingVMType, setEditingVMType] = useState<VMType | null>(null);
  const [formData, setFormData] = useState<VMTypeFormData>({
    vmName: '', cpu: '', ram: '', isActive: true, displayOrder: 0,
  });
  const [confirmDialog, setConfirmDialog] = useState<{ open: boolean; id: number }>({ open: false, id: 0 });

  useEffect(() => { fetchVMTypes(); }, []);

  const fetchVMTypes = async () => {
    try {
      const response = await vmTypesAPI.getAll();
      setVMTypes(response.data);
      setError('');
    } catch (err) {
      setError('Failed to load VM types');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (vmType: VMType | null = null) => {
    if (vmType) {
      setEditingVMType(vmType);
      setFormData({ vmName: vmType.VMName, cpu: vmType.CPU, ram: vmType.RAM, isActive: !!vmType.IsActive, displayOrder: vmType.DisplayOrder });
    } else {
      setEditingVMType(null);
      setFormData({ vmName: '', cpu: '', ram: '', isActive: true, displayOrder: 0 });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingVMType(null);
    setFormData({ vmName: '', cpu: '', ram: '', isActive: true, displayOrder: 0 });
  };

  const handleSubmit = async () => {
    try {
      if (editingVMType) {
        await vmTypesAPI.update(editingVMType.VMTypeId, formData);
      } else {
        await vmTypesAPI.create(formData);
      }
      fetchVMTypes();
      handleCloseDialog();
      setError('');
    } catch (err: unknown) {
      const axiosErr = err as { response?: { data?: { error?: string } } };
      setError(axiosErr.response?.data?.error || 'Failed to save VM type');
    }
  };

  const handleDeleteClick = (id: number) => {
    setConfirmDialog({ open: true, id });
  };

  const handleDeleteConfirm = async () => {
    const id = confirmDialog.id;
    setConfirmDialog({ open: false, id: 0 });
    try {
      await vmTypesAPI.delete(id);
      fetchVMTypes();
      setError('');
    } catch (err) {
      setError('Failed to delete VM type');
    }
  };

  if (loading) return <Typography>Loading...</Typography>;

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h4">VM Types</Typography>
        <Button variant="contained" color="secondary" size="small" startIcon={<AddIcon fontSize="small" />} onClick={() => handleOpenDialog()}>Add VM Type</Button>
      </Box>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>VM Name</TableCell><TableCell>CPU (vCores)</TableCell><TableCell>RAM (GB)</TableCell>
              <TableCell>Active</TableCell><TableCell>Display Order</TableCell><TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {vmTypes.map((vmType) => (
              <TableRow key={vmType.VMTypeId}>
                <TableCell>{vmType.VMName}</TableCell><TableCell>{vmType.CPU}</TableCell><TableCell>{vmType.RAM}</TableCell>
                <TableCell>{vmType.IsActive ? 'Yes' : 'No'}</TableCell><TableCell>{vmType.DisplayOrder}</TableCell>
                <TableCell align="right">
                  <IconButton onClick={() => handleOpenDialog(vmType)} size="small"><EditIcon fontSize="small" /></IconButton>
                  <IconButton onClick={() => handleDeleteClick(vmType.VMTypeId)} size="small" color="error"><DeleteIcon fontSize="small" /></IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>{editingVMType ? 'Edit VM Type' : 'Add VM Type'}</DialogTitle>
        <DialogContent>
          <TextField autoFocus margin="dense" size="small" label="VM Name" fullWidth value={formData.vmName} onChange={(e) => setFormData({ ...formData, vmName: e.target.value })} required />
          <TextField margin="dense" size="small" label="CPU (vCores)" type="number" fullWidth value={formData.cpu} onChange={(e) => setFormData({ ...formData, cpu: parseInt(e.target.value) || '' })} required />
          <TextField margin="dense" size="small" label="RAM (GB)" type="number" fullWidth value={formData.ram} onChange={(e) => setFormData({ ...formData, ram: parseInt(e.target.value) || '' })} required />
          <TextField margin="dense" size="small" label="Display Order" type="number" fullWidth value={formData.displayOrder} onChange={(e) => setFormData({ ...formData, displayOrder: parseInt(e.target.value) || 0 })} />
          <FormControlLabel control={<Switch checked={formData.isActive} onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })} />} label="Active" />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} size="small">Cancel</Button>
          <Button onClick={handleSubmit} variant="contained" color="secondary" disabled={!formData.vmName || !formData.cpu || !formData.ram}>
            {editingVMType ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
      <ConfirmDialog
        open={confirmDialog.open}
        title="Deactivate VM Type"
        message="Are you sure you want to deactivate this VM type?"
        confirmLabel="Deactivate"
        confirmColor="error"
        onConfirm={handleDeleteConfirm}
        onCancel={() => setConfirmDialog({ open: false, id: 0 })}
      />
    </Box>
  );
};

export default VMTypes;
